import { BookOpen, Check, Eye, LogOut, MessageCircleQuestion, Rows3, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { Me } from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { applyDensity, Button, type Density, Menu, MenuItem, MenuLabel, MenuSeparator, MenuText, readDensity, writeDensity } from '@/kit';
import { useCoreWowFlag } from '@/features/wow/core-flags';
import { ShowQaMenuItem } from './ShowQaToggle';
import { useExperience } from '@/app/experience/ExperienceProvider';
import { useViewAs } from '@/app/experience/experience-api';
import { BEGINNER, EXPERT } from '@/app/experience/tiers';
import { ViewAsModal } from '@/app/view-as/ViewAsModal';
import { useTheme } from '@/hooks/useTheme';
import { useAskLabel } from '@/features/ask';
import { usePhoneWidth } from '@/kit/PageShell';
import { THEME_ICON, THEME_LABEL } from './ThemeToggle';
import { CONCEPT_GUIDE_DESCRIPTION, CONCEPT_GUIDE_TITLE, resolveConceptGuideHref } from './concept-guide';

/** "Jane Doe" → "JD"; an email or a single word gives its first two letters. */
export function initials(name: string): string {
  const words = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '';
  return (words.length === 1 ? words[0]!.slice(0, 2) : `${words[0]![0]}${words.at(-1)![0]}`).toUpperCase();
}
const firstName = (name: string) => name.replace(/@.*/, '').split(/\s+/)[0] ?? name;

export function UserMenu({ me, onAsk }: { me?: Me; onAsk?: () => void }) {
  const { actorLabel, signOut, mode } = useAuth();
  const { preference, toggleTheme } = useTheme();
  const ThemeIcon = THEME_ICON[preference];
  const askLabel = useAskLabel();
  const phone = usePhoneWidth();
  const navigate = useNavigate();
  const name = me?.displayName ?? actorLabel ?? 'Signed in';
  const scopes = me?.roles.filter((r) => r.scopeName).map((r) => `${r.role} · ${r.scopeName}`) ?? [];
  const globals = me?.roles.filter((r) => !r.scopeName).map((r) => r.role) ?? [];

  // R-13 wow (wow.density): compact or comfortable, remembered for this user in this browser.
  const densityOn = useCoreWowFlag('density');
  const [density, setDensity] = useState<Density>(() => readDensity(me?.id));
  useEffect(() => setDensity(readDensity(me?.id)), [me?.id]);
  const choose = (d: Density) => {
    setDensity(d);
    writeDensity(me?.id, d);
    applyDensity(d);
  };

  // R-14 T-087: Expert mode switches between Beginner (tier 1, the simple set) and Expert (tier 4,
  // everything the roles allow); remembered per user by the API. Tiers 2 and 3 arrive in round 15.
  const { tier, setTier, saving, ready } = useExperience();
  const expert = tier >= EXPERT;
  // R-14 T-088: View as, for Administrators where the server allows it (never on PROD)
  const viewAs = useViewAs(Boolean(me));
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const canViewAs = Boolean(viewAs.data?.allowed) && !viewAs.data?.active;

  return (
    <>
    <Menu
      align="end"
      aria-label="Account"
      trigger={
        <Button variant="quiet" className="k-user">
          {/* #727: the user chip — a Clover disc with initials (Bog on Clover, an accessible pair) */}
          <span aria-hidden className="k-user-disc">
            {initials(name) || <UserRound size={14} />}
          </span>
          <span className="k-user-name">{firstName(name)}</span>
        </Button>
      }
    >
      <MenuLabel>{name}</MenuLabel>
      {me?.email ? <MenuText>{me.email}</MenuText> : null}
      <MenuSeparator />
      <MenuText>
        {globals.length > 0 ? <span style={{ display: 'block' }}>{globals.join(' · ')}</span> : null}
        {scopes.map((s) => (
          <span key={s} style={{ display: 'block' }}>
            {s}
          </span>
        ))}
        {globals.length === 0 && scopes.length === 0 ? <span style={{ display: 'block' }}>No roles assigned yet</span> : null}
        <span style={{ display: 'block' }}>
          Sign-in mode: <span className="id-mono">{mode}</span>
        </span>
      </MenuText>
      {/* v1.2 change 01: below 768 the bar keeps only notifications and the account; Ask and theme live here. Mounted only
          there (PR #85 review): hidden items would still be keyboard and typeahead stops */}
      {phone ? (
        <>
          <MenuSeparator />
          {onAsk ? (
            <MenuItem onClick={onAsk} data-testid="account-ask">
              <MessageCircleQuestion size={14} aria-hidden />
              {askLabel}
            </MenuItem>
          ) : null}
          <MenuItem onClick={toggleTheme} data-testid="account-theme">
            <ThemeIcon size={14} aria-hidden />
            {THEME_LABEL[preference]}
          </MenuItem>
        </>
      ) : null}
      <MenuSeparator />
      <MenuLabel>Help</MenuLabel>
      <MenuItem onClick={() => void resolveConceptGuideHref().then((href) => navigate(href))}>
        <BookOpen size={14} aria-hidden />
        <span style={{ display: 'grid' }}>
          <span>{CONCEPT_GUIDE_TITLE}</span>
          <span className="k-meta">{CONCEPT_GUIDE_DESCRIPTION}</span>
        </span>
      </MenuItem>
      {densityOn ? (
        <>
          <MenuSeparator />
          <MenuLabel>Density</MenuLabel>
          {(['compact', 'comfortable'] as const).map((d) => (
            <MenuItem key={d} onClick={() => choose(d)}>
              {density === d ? <Check size={14} aria-hidden /> : <Rows3 size={14} aria-hidden />}
              {d === 'compact' ? 'Compact density' : 'Comfortable density'}
              {density === d ? <span className="sr-only"> (current)</span> : null}
            </MenuItem>
          ))}
        </>
      ) : null}
      <MenuSeparator />
      {/* offered once the person's tier is known, so its state is never a placeholder; not while previewing — your own
          Experience is kept during a View-as preview (rule view_as_preferences_are_yours) */}
      {ready && !viewAs.data?.active ? (
      <MenuItem data-testid="expert-mode" checked={expert} onClick={() => !saving && void setTier(expert ? BEGINNER : EXPERT).catch((e: unknown) => toast.error('Expert mode was not changed', { description: e instanceof Error ? e.message : String(e) }))}>
        <span style={{ display: 'grid' }}>
          <span>Expert mode</span>
          {/* aria-checked carries the state; the name stays exactly "Expert mode" */}
          <span className="k-meta" aria-hidden>
            {expert ? 'Everything your role allows' : 'The simple set'}
          </span>
        </span>
      </MenuItem>
      ) : null}
      {canViewAs ? (
        <MenuItem onClick={() => setViewAsOpen(true)}>
          <Eye size={14} aria-hidden />
          View as…
        </MenuItem>
      ) : null}
      <ShowQaMenuItem roles={globals} />
      <MenuSeparator />
      <MenuItem onClick={signOut}>
        <LogOut size={14} aria-hidden />
        {mode === 'dev' ? 'Switch seeded user' : 'Sign out'}
      </MenuItem>
    </Menu>
    {viewAsOpen ? <ViewAsModal open onClose={() => setViewAsOpen(false)} /> : null}
    </>
  );
}
